import { OBJECT_TYPE_ICONS, SOURCE_CONFIGS } from '../constants/migrationConstants';
import { PROGRESS_MESSAGES, ERROR_MESSAGES } from '../constants/stringConstants';
import { fetchSailpointConnection, fetchOIMConnection } from '../redux/migrationThunks';

export const createConfigObjectTypes = (counts) =>
  counts.map((item) => ({
    type: item.objectType,
    icon: OBJECT_TYPE_ICONS[item.objectType] || '⚙️',
    label: item.objectType.replace(/([A-Z])/g, ' $1').trim(),
    description: `Migrate ${item.objectType.toLowerCase()} configurations`,
  }));

export const filterConfigObjects = (objects) => {
  // Filter out data objects, keep only configuration objects
  const dataObjectTypes = [
    // SailPoint data objects
    'Identity',
    'Account',
    'Entitlement',
    'Link',
    // OIM data objects (lowercase)
    'accounts',
    'users',
    'entitlements',
    'groups',
  ];
  return objects.filter((item) => !dataObjectTypes.includes(item.objectType));
};

export const calculateTotalConfigObjects = (objects) =>
  filterConfigObjects(objects).reduce((sum, item) => sum + item.count, 0);

export const cleanEndpointConfig = (config) => {
  // Remove owner fields that cause "Resource Owner not found" errors
  const cleanEndpoint = { ...config.endpoint };
  delete cleanEndpoint.owner;
  delete cleanEndpoint.ownerType;
  delete cleanEndpoint.resourceOwner;
  delete cleanEndpoint.resourceOwnerType;
  return cleanEndpoint;
};

export const formatMigrationStatusMessage = (success, message) => {
  if (success) {
    return `Successfully onboarded to Saviynt: ${message || 'Application created successfully'}`;
  }
  return `Onboarding failed: ${message || 'Unknown error'}`;
};

// Cache for source IDs, details, and connection names
const sourceIdCache = new Map();
const sourceDetailsCache = new Map();
const connectionNameCache = new Map();
const pendingRequests = new Map(); // Prevent duplicate API calls

// Use the new migration config API to get source ID
export const getSourceIdFromType = async (sourceType, dispatch = null, selectedSourceId = null) => {
  const baseType = sourceType
  const cacheKey = `${baseType}_${selectedSourceId || 'primary'}`;

  // Return cached result if available
  if (sourceIdCache.has(cacheKey)) {
    return sourceIdCache.get(cacheKey);
  }

  // Return pending request if already in progress
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Try to get from Redux store first (if connection details already fetched)
  if (dispatch) {
    try {
      const state = dispatch((_, getState) => getState());
      const connectionDetails = state?.migration?.connectionDetails?.[baseType];
      if (connectionDetails?.sourceId) {
        sourceIdCache.set(cacheKey, connectionDetails.sourceId);
        return connectionDetails.sourceId;
      }
    } catch (e) {
      // Continue to API call
    }
  }

  // Create and cache the API request promise
  const apiRequest = (async () => {
    try {
      if (!dispatch) {
        throw new Error(ERROR_MESSAGES.DISPATCH_REQUIRED);
      }

      // Call different APIs based on source type
      let configResponse;
      if (baseType === 'sailpoint-migration') {
        configResponse = await dispatch(fetchSailpointConnection()).unwrap();
      } else if (baseType === 'oim-migration') {
        configResponse = await dispatch(fetchOIMConnection()).unwrap();
      }

      // Extract sourceId and cache connectionName
      const sourceId = configResponse?.data?.sourceId;
      const connectionName = configResponse?.data?.connectionName;
      
      if (connectionName) {
        connectionNameCache.set(baseType, connectionName);
      }

      if (!sourceId) {
        sourceIdCache.set(cacheKey, null);
        return null;
      }
      sourceIdCache.set(cacheKey, sourceId);
      return sourceId;
    } catch (error) {
      sourceIdCache.set(cacheKey, null);
      return null;
    } finally {
      // Remove from pending requests when done
      pendingRequests.delete(cacheKey);
    }
  })();

  // Cache the promise to prevent duplicate requests
  pendingRequests.set(cacheKey, apiRequest);
  return apiRequest;
};

// Fetch source details including URL from backend
export const getSourceDetails = async (sourceType, dispatch = null) => {
  const baseType = sourceType?.replace('-migration', '') || 'sailpoint';
  const cacheKey = `${baseType}_details`;

  if (sourceDetailsCache.has(cacheKey)) {
    return sourceDetailsCache.get(cacheKey);
  }

  try {
    const sourceId = await getSourceIdFromType(sourceType, dispatch);
    if (!sourceId) return null;

    // Get connection name from cache if available
    const connectionName = connectionNameCache.get(baseType) || sourceId;

    const details = {
      connectionName,
      connectionDetails: {},
    };

    sourceDetailsCache.set(cacheKey, details);
    return details;
  } catch (error) {
    return null;
  }
};

// Check if source is configured
export const isSourceConfigured = async (sourceType, dispatch = null) => {
  const sourceId = await getSourceIdFromType(sourceType, dispatch);
  return sourceId !== null;
};

// Progress tracking utilities
export const createProgressTracker = (totalSteps, onUpdate) => {
  let currentStep = 0;

  return {
    nextStep: (message) => {
      currentStep += 1;
      const percentage = Math.round((currentStep / totalSteps) * 100);
      onUpdate(percentage, message, currentStep, totalSteps);
      return percentage;
    },
    setStep: (step, message) => {
      currentStep = step;
      const percentage = Math.round((currentStep / totalSteps) * 100);
      onUpdate(percentage, message, currentStep, totalSteps);
      return percentage;
    },
    complete: (message = PROGRESS_MESSAGES.PROCESSING) => {
      currentStep = totalSteps;
      onUpdate(100, message, totalSteps, totalSteps);
      return 100;
    },
  };
};

// Discovery progress steps
const DISCOVERY_MESSAGE = PROGRESS_MESSAGES.DISCOVERY_IN_PROGRESS;
export const DISCOVERY_STEPS = {
  SAILPOINT: [DISCOVERY_MESSAGE],
  OIM: [DISCOVERY_MESSAGE],
};

// Configuration migration progress steps
export const CONFIG_MIGRATION_STEPS = {
  APPLICATION: [
    'Validating application configuration...',
    'Mapping attributes and schemas...',
    'Creating connection parameters...',
    'Configuring provisioning rules...',
    'Testing connectivity...',
    'Finalizing application setup...',
  ],
  WORKFLOW: [
    'Analyzing workflow structure...',
    'Converting workflow steps...',
    'Mapping approval processes...',
    'Configuring notifications...',
    'Validating workflow logic...',
    'Deploying workflow configuration...',
  ],
  RULE: [
    'Parsing rule syntax...',
    'Converting rule logic...',
    'Validating rule conditions...',
    'Testing rule execution...',
    'Optimizing rule performance...',
    'Deploying rule configuration...',
  ],
};

// Simulate step-by-step progress with realistic delays
export const simulateProgressSteps = async (steps, onProgress, baseDelay = 800) => {
  const tracker = createProgressTracker(steps.length, onProgress);

  const processStep = async (index) => {
    const step = steps[index];
    tracker.setStep(index + 1, step);

    // Variable delay to simulate real processing
    const delay = baseDelay + Math.random() * 400; // 800-1200ms
    await new Promise((resolve) => { setTimeout(resolve, delay); });
  };

  await Promise.all(steps.map((_, index) => processStep(index)));

  return tracker.complete();
};

export const getBaseSourceType = (sourceType) =>
  sourceType?.replace('-migration', '') || 'sailpoint';

export const getSourceConfig = (sourceType) => {
  const baseType = sourceType?.replace('-migration', '') || 'sailpoint';
  return SOURCE_CONFIGS[baseType] || SOURCE_CONFIGS.sailpoint;
};
// Get appropriate progress steps based on source type and operation
export const getProgressSteps = (sourceType, operation, objectType = null) => {
  const baseType = getBaseSourceType(sourceType);

  switch (operation) {
    case 'discovery':
      return baseType === 'oim' ? DISCOVERY_STEPS.OIM : DISCOVERY_STEPS.SAILPOINT;

    case 'migration':
      if (objectType) {
        return (
          CONFIG_MIGRATION_STEPS[objectType.toUpperCase()] || CONFIG_MIGRATION_STEPS.APPLICATION
        );
      }
      return CONFIG_MIGRATION_STEPS.APPLICATION;

    default:
      return [PROGRESS_MESSAGES.PROCESSING, PROGRESS_MESSAGES.COMPLETING_OPERATION];
  }
};
